import { localStorageService, StorageKeys, sessionStorageService } from 'shared/lib/storage/storageService';

import type { EnvType } from './types';

type ImpersonationHeaders = {
  'X-Dashboard-Impersonation-Id'?: string;
};
type DownloadAcceptHeaders = 'application/pdf' | 'application/zip' | 'text/csv';
type CallbackVoid = (response: any) => void;
type CallbackPromiseString = () => Promise<string>;

type ApiHeaders = {
  'Content-Type'?: string;
  'Accept'?: string;
  'Authorization'?: string;
  'X-Dashboard-Company-Id'?: string;
  'X-Dashboard-Key-Env': EnvType;
  'X-Dashboard-Impersonation-Id'?: string;
};

const AuthenticationRequiredException = new Error('AuthenticationRequiredException');

export type ServerErrorResponse = {
  status: number;
  data: unknown;
};

export const isServerError = (error: unknown): error is ServerErrorResponse => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number' &&
    error.status >= 500
  );
};

export class BaseApiClient {
  private env: EnvType = 'sandbox';
  private tenantCompanyId = '';
  private callbackOn401: CallbackVoid | null = null;
  private callbackGetAuthToken: CallbackPromiseString = () => Promise.resolve('');

  constructor(private apiRoot: string) {}

  setCallbackAuthToken(callback: CallbackPromiseString) {
    this.callbackGetAuthToken = callback;
  }

  setEnv(env: EnvType) {
    this.env = env;
  }

  setCallbackOn401(callback: CallbackVoid) {
    this.callbackOn401 = callback;
  }

  setTenantCompanyId(tenantCompanyId: string) {
    this.tenantCompanyId = tenantCompanyId;
  }

  protected async getAuthHeaders() {
    const token = await this.callbackGetAuthToken();

    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : null;
  }

  protected getImpersonationHeaders = (): ImpersonationHeaders => {
    const impersonationId = localStorageService.get(StorageKeys.IMPERSONATION_ID);

    if (impersonationId) {
      return {
        'X-Dashboard-Impersonation-Id': impersonationId,
      };
    }

    return {};
  };

  protected getEnvHeaders = () => {
    return { 'X-Dashboard-Key-Env': this.env };
  };

  protected getTenantCompanyHeaders = () => {
    return this.tenantCompanyId ? { 'X-Dashboard-Company-Id': this.tenantCompanyId } : {};
  };

  protected getSignupSourceHeaders = (): Record<string, string> => {
    // Read-and-clear: the header only matters for new-account creation on the
    // first authenticated API call. Consuming it here keeps it out of later
    // requests and avoids cross-user contamination if a tab is reused after logout.
    // Uses sessionStorageService (same as the write in App.tsx) so the value
    // round-trips through JSON-parse without the raw quote characters leaking
    // into the header.
    const source = sessionStorageService.get(StorageKeys.SIGNUP_SOURCE);
    if (typeof source !== 'string' || !source) {
      return {};
    }
    sessionStorageService.remove(StorageKeys.SIGNUP_SOURCE);
    return { 'X-Signup-Source': source };
  };

  protected getSignupUseCaseHeaders = (): Record<string, string> => {
    // Same read-and-clear lifecycle as the source header. Backend applies on
    // user creation only; subsequent calls ignore the value.
    const useCase = sessionStorageService.get(StorageKeys.SIGNUP_USE_CASE);
    if (typeof useCase !== 'string' || !useCase) {
      return {};
    }
    sessionStorageService.remove(StorageKeys.SIGNUP_USE_CASE);
    return { 'X-Signup-Use-Case': useCase };
  };

  protected async mergeHeaders(headers: Partial<ApiHeaders>): Promise<ApiHeaders> {
    return {
      ...headers,
      ...(await this.getAuthHeaders()),
      ...this.getImpersonationHeaders(),
      ...this.getEnvHeaders(),
      ...this.getTenantCompanyHeaders(),
      ...this.getSignupSourceHeaders(),
      ...this.getSignupUseCaseHeaders(),
    };
  }

  protected async getHeaders(): Promise<ApiHeaders> {
    return this.mergeHeaders({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    });
  }

  protected async getDownloadHeaders(accept: DownloadAcceptHeaders, extraDownloadHeaders?: Record<string, string>) {
    return this.mergeHeaders({
      Accept: accept,
      ...extraDownloadHeaders,
    });
  }

  protected getAbsoluteUrl = (url: string) => {
    if (url.startsWith('http')) {
      return url;
    }

    return `${this.apiRoot}${url}`;
  };

  request<T>(url: string, options?: Partial<RequestInit>, params?: any): Promise<T> {
    const u = new URL(this.getAbsoluteUrl(url));

    // TODO: just naive implementation
    Object.keys(params || {})
      .filter((key) => params[key] !== null && params[key] !== undefined)
      .forEach((key) => {
        const currentValue = params[key];

        if (Array.isArray(currentValue)) {
          currentValue.forEach((val) => {
            u.searchParams.append(key, val);
          });
        } else {
          u.searchParams.append(key, currentValue);
        }
      });

    function isOwnServer(lastUrl: string, apiRoot: string) {
      return lastUrl.startsWith(apiRoot);
    }

    return fetch(u.href, {
      credentials: 'omit',
      ...options,
    }).then((resp) => {
      if (resp.status === 204) {
        return null;
      }

      if (resp.status < 299 && resp.status >= 200) {
        if (params && params.responseType === 'blob') {
          return resp.blob();
        }

        return resp.json();
      }

      //session has expired
      if (resp.status === 401 && isOwnServer(u.toString(), this.apiRoot)) {
        if (!this.callbackOn401) {
          // eslint-disable-next-line no-console
          console.warn('use `this.callbackOn401` for general callback on 401');
          throw AuthenticationRequiredException;
        }

        const callback = this.callbackOn401;

        return resp.json().then((data) => {
          callback(data);
          throw data;
        });
      }

      if (resp.status >= 500) {
        return resp
          .json()
          .catch(() => {
            throw { status: resp.status, data: null } satisfies ServerErrorResponse;
          })
          .then((data) => {
            throw { status: resp.status, data } satisfies ServerErrorResponse;
          });
      }

      return resp.json().then((data) => {
        throw data;
      });
    });
  }

  async patch<T, K = FormData | Record<string, unknown>>(url: string, data: K): Promise<T> {
    const headers = await this.getHeaders();

    let body: string | FormData = '';

    if (data instanceof FormData) {
      body = data;
      delete (headers as ApiHeaders)['Content-Type'];
    } else if (data) {
      body = JSON.stringify(data);
    } else {
      delete (headers as ApiHeaders)['Content-Type'];
    }

    return this.request<T>(url, {
      headers,
      body,
      method: 'PATCH',
    });
  }

  async delete<T>(url: string, data: any = undefined): Promise<T> {
    const headers = await this.getHeaders();

    let body: BodyInit | null = null;

    if (data) {
      body = JSON.stringify(data);
    }

    return this.request<T>(url, {
      headers,
      body,
      method: 'DELETE',
    });
  }

  async post<T, K = FormData | Record<string, unknown>>(url: string, data: K): Promise<T> {
    const headers = await this.getHeaders();

    let body: string | FormData = '';

    if (data instanceof FormData) {
      body = data;
      delete (headers as ApiHeaders)['Content-Type'];
    } else if (data) {
      body = JSON.stringify(data);
    } else {
      delete (headers as ApiHeaders)['Content-Type'];
    }

    return this.request<T>(url, {
      headers,
      body,
      method: 'POST',
    });
  }

  async put<T, K = FormData | Record<string, unknown>>(url: string, data: K): Promise<T> {
    const headers = await this.getHeaders();

    let body: string | FormData = '';

    if (data instanceof FormData) {
      body = data;
      delete (headers as ApiHeaders)['Content-Type'];
    } else if (data) {
      body = JSON.stringify(data);
    } else {
      delete (headers as ApiHeaders)['Content-Type'];
    }

    return this.request<T>(url, {
      headers,
      body,
      method: 'PUT',
    });
  }

  async get<T>(url: string, params?: unknown, options?: Partial<RequestInit>): Promise<T> {
    const headers = await this.getHeaders();

    return this.request(
      url,
      {
        headers,
        method: 'GET',
        ...options,
      },
      params,
    );
  }

  async download<T>(
    url: string,
    params: Record<string, unknown> = {},
    method: 'GET' | 'POST' = 'GET',
    accept: DownloadAcceptHeaders = 'application/pdf',
  ): Promise<T> {
    const headers = await this.getDownloadHeaders(accept);

    return this.request(
      url,
      {
        headers,
        method,
      },
      {
        responseType: 'blob',
        ...params,
      },
    );
  }

  async downloadWithStatus<T>(
    url: string,
    params: Record<string, unknown> = {},
    method: 'GET' | 'POST' = 'GET',
    accept: DownloadAcceptHeaders = 'application/pdf',
  ): Promise<{ status: number; data: T | null }> {
    const headers = await this.getDownloadHeaders(accept);

    const u = new URL(this.getAbsoluteUrl(url));
    for (const key of Object.keys(params)) {
      const currentValue = params[key];
      if (currentValue === null || currentValue === undefined) continue;
      if (Array.isArray(currentValue)) {
        for (const val of currentValue) {
          u.searchParams.append(key, val);
        }
      } else {
        u.searchParams.append(key, String(currentValue));
      }
    }

    const resp = await fetch(u.href, {
      credentials: 'omit',
      headers,
      method,
    });

    if (resp.status === 202) {
      return { status: resp.status, data: null };
    }

    if (!resp.ok) {
      const data = await resp.json().catch(() => null);
      throw { status: resp.status, data } satisfies ServerErrorResponse;
    }

    const blob = await resp.blob();
    return { status: resp.status, data: blob as T };
  }
}

export const baseApiClient = new BaseApiClient(import.meta.env.VITE_APP_API_URL ?? '');
